import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { User, Shield, Eye, EyeOff, Info } from "lucide-react";
import { AboutAppDialog } from "../components/AboutAppDialog";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  
  // Admin form state
  const [adminForm, setAdminForm] = useState({ username: "", password: "" });
  
  // Employee form state
  const [employeeForm, setEmployeeForm] = useState({ matricula: "", cpf: "" });

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    
    if (!adminForm.username || !adminForm.password) {
      toast.error("Preencha todos os campos");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, adminForm);
      const { token, user_type, name } = response.data;
      
      login({ user_type, name }, token);
      toast.success("Login realizado com sucesso!");
      if (user_type === "tst") {
        navigate("/tst");
      } else if (user_type === "cobranca") {
        navigate("/cobranca");
      } else {
        navigate("/admin");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenciais inválidas");
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeLogin = async (e) => {
    e.preventDefault();
    
    if (!employeeForm.matricula || !employeeForm.cpf) {
      toast.error("Preencha todos os campos");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/employee/login`, employeeForm);
      const { token, user_type, name, matricula, has_signature, whatsapp_status, is_supervisor } = response.data;
      const userToStore = { user_type, name, matricula, has_signature, whatsapp_status, is_supervisor };
      login(userToStore, token);
      toast.success(`Bem-vindo, ${name}!`);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Matrícula ou CPF inválidos");
    } finally {
      setLoading(false);
    }
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Background Pattern - subtle dark */}
      <div className="absolute inset-0 opacity-[0.04]">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230f172a' fill-opacity='0.6'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      {/* Soft blue accent blob top-right */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-blue-100/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-slate-200/30 blur-3xl pointer-events-none" />
      
      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo/Header - Responsive sizing */}
        <div className="text-center mb-4 sm:mb-8">
          <img 
            src="/logo_full.png" 
            alt="AVI Segurança"
            className="block mx-auto w-56 sm:w-80 md:w-[28rem] h-auto"
          />
          <p className="text-slate-700 mt-2 text-sm sm:text-base tracking-wider font-medium">
            PORTAL DO FUNCIONÁRIO
          </p>
        </div>

        <Card className="bg-white/95 backdrop-blur shadow-2xl border-0">
          <CardHeader className="space-y-1 pb-2 sm:pb-4 pt-4 sm:pt-6">
            <CardTitle className="text-lg sm:text-xl font-heading text-center">
              Entrar
            </CardTitle>
            <CardDescription className="text-center text-xs sm:text-sm">
              Selecione seu tipo de acesso
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-4 sm:pb-6">
            <Tabs defaultValue="employee" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4 sm:mb-6">
                <TabsTrigger 
                  value="employee" 
                  className="data-[state=active]:bg-slate-900 data-[state=active]:text-white text-sm sm:text-base"
                  data-testid="employee-tab"
                >
                  <User className="w-4 h-4 mr-2" />
                  Funcionário
                </TabsTrigger>
                <TabsTrigger 
                  value="admin"
                  className="data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                  data-testid="admin-tab"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </TabsTrigger>
              </TabsList>

              {/* Employee Login */}
              <TabsContent value="employee">
                <form onSubmit={handleEmployeeLogin} className="space-y-3 sm:space-y-4">
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="matricula" className="text-sm">Matrícula</Label>
                    <Input
                      id="matricula"
                      data-testid="matricula-input"
                      placeholder="Digite sua matrícula"
                      value={employeeForm.matricula}
                      onChange={(e) => setEmployeeForm({ 
                        ...employeeForm, 
                        matricula: e.target.value.replace(/\D/g, "").slice(0, 5)
                      })}
                      maxLength={5}
                      className="font-mono text-base sm:text-lg tracking-wider h-10 sm:h-12"
                      autoComplete="username"
                      inputMode="numeric"
                      required
                    />
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="cpf" className="text-sm">CPF (Senha)</Label>
                    <div className="relative">
                      <Input
                        id="cpf"
                        data-testid="cpf-input"
                        type={showPassword ? "text" : "password"}
                        placeholder="Digite seu CPF"
                        value={employeeForm.cpf}
                        onChange={(e) => setEmployeeForm({ 
                          ...employeeForm, 
                          cpf: formatCPF(e.target.value)
                        })}
                        maxLength={14}
                        className="pr-10 h-10 sm:h-12"
                        autoComplete="current-password"
                        inputMode="numeric"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">
                      Sua senha é o seu CPF
                    </p>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-10 sm:h-12 bg-slate-900 hover:bg-slate-800 text-sm sm:text-base"
                    disabled={loading}
                    data-testid="employee-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        Entrando...
                      </span>
                    ) : (
                      "Entrar"
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Admin Login */}
              <TabsContent value="admin">
                <form onSubmit={handleAdminLogin} className="space-y-3 sm:space-y-4">
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="admin-username" className="text-sm">Usuário</Label>
                    <Input
                      id="admin-username"
                      data-testid="admin-username-input"
                      placeholder="Digite seu usuário"
                      value={adminForm.username}
                      onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value })}
                      className="h-10 sm:h-12"
                      autoComplete="username"
                      required
                    />
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="admin-password" className="text-sm">Senha</Label>
                    <div className="relative">
                      <Input
                        id="admin-password"
                        data-testid="admin-password-input"
                        type={showPassword ? "text" : "password"}
                        placeholder="Digite sua senha"
                        value={adminForm.password}
                        onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                        className="pr-10 h-10 sm:h-12"
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
                      </button>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-10 sm:h-12 bg-slate-900 hover:bg-slate-800 text-sm sm:text-base"
                    disabled={loading}
                    data-testid="admin-login-btn"
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                        Entrando...
                      </span>
                    ) : (
                      "Entrar"
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="text-center mt-3 sm:mt-6 space-y-1">
          <p className="text-slate-600 text-xs">
            DESENVOLVIDO POR DARK HORSE TECNOLOGIA ®
          </p>
          <button
            type="button"
            onClick={() => setAboutOpen(true)}
            className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700 text-[11px] tracking-wider underline-offset-2 hover:underline transition"
            data-testid="login-about-app-btn"
          >
            <Info className="w-3 h-3" />
            SOBRE O APP
          </button>
        </div>
      </motion.div>

      <AboutAppDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </div>
  );
};

export default LoginPage;
